// =============================================================================
// Label manager modal: the public-holidays switch with its reserved colour,
// then every label of the current calendar — reserved ones (birthday,
// to-dos) read-only, the rest renamable inline. Calendar.js owns the label
// rows and the settings; this renders and edits them.
// =============================================================================

import React from 'react';
import { X, Gift, CheckSquare } from 'lucide-react';
import { useTranslation } from '../../lib/i18n';

const LabelManager = ({
  onClose,
  holidaySettings,
  updateHolidaySettings,
  categories,
  birthdaySettings,
  sortedCategories,
  updateLabel,
  showHolidayColorPicker,
  setShowHolidayColorPicker
}) => {
  const { t } = useTranslation();
  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={() => onClose()}
    >
      <div
        className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold">{t('calendar.labelManager')}</h3>
          <button
            onClick={() => onClose()}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-4 overflow-y-auto space-y-2">
        {(
          <div className="mb-2 rounded-xl border border-gray-200 p-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">
                  {t('calendar.holidays', 'Österreichische Feiertage')}
                </p>
                <p className="text-xs text-gray-500">
                  {t('calendar.holidaysHint', 'Zeigt die gesetzlichen Feiertage im Kalender. Die gewählte Label-Farbe ist dann dafür reserviert, wie beim Geburtstags-Label.')}
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={!!holidaySettings.enabled}
                onClick={() => updateHolidaySettings({ enabled: !holidaySettings.enabled })}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                  holidaySettings.enabled ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    holidaySettings.enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            {holidaySettings.enabled && (
              <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                <span className="text-sm text-gray-600">{t('calendar.labelColor', 'Label-Farbe')}</span>
                <div className="relative">
                  {(() => {
                    const sel = categories.find((c) => c.id === holidaySettings.categoryId) || categories[0];
                    return (
                      <button
                        type="button"
                        onClick={() => setShowHolidayColorPicker((v) => !v)}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-sm"
                      >
                        <span className="w-3 h-3 rounded-full" style={{ backgroundColor: sel?.color }} />
                        <span className="font-medium" style={{ color: sel?.color }}>
                          {t('calendar.holidaysLabel', 'Feiertage')}
                        </span>
                      </button>
                    );
                  })()}
                  {showHolidayColorPicker && (
                    <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-2 z-20 w-48 max-h-64 overflow-y-auto">
                      {categories
                        .filter((c) => !(birthdaySettings.linkedToCalendar && c.id === birthdaySettings.categoryId))
                        .map((cat) => (
                          <button
                            key={cat.id}
                            type="button"
                            onClick={() => {
                              updateHolidaySettings({ categoryId: cat.id });
                              setShowHolidayColorPicker(false);
                            }}
                            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-gray-50 ${
                              cat.id === holidaySettings.categoryId ? 'bg-gray-100 font-medium' : ''
                            }`}
                          >
                            <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color }} />
                            <span style={{ color: cat.color }}>{cat.name}</span>
                            {cat.id === holidaySettings.categoryId && <span className="ml-auto text-primary-600">✓</span>}
                          </button>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
          {sortedCategories.map((cat) => (
            <div
              key={cat.id}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50"
            >
              {/* Color indicator - dashed if unset */}
              <div
                className={`w-5 h-5 rounded-full flex-shrink-0 ${
                  cat.isCustom ? '' : 'border-2 border-dashed'
                }`}
                style={
                  cat.isCustom
                    ? { backgroundColor: cat.color }
                    : { borderColor: cat.color, backgroundColor: 'transparent' }
                }
              />
              {cat.isBirthdayLabel || cat.isTodoLabel ? (
                <div className="flex-1 flex items-center gap-2 text-sm font-medium" style={{ color: cat.color }}>
                  {cat.isBirthdayLabel ? <Gift size={14} /> : <CheckSquare size={14} />}
                  <span className="truncate">
                    {cat.isBirthdayLabel ? t('birthday.label') : cat.name}
                  </span>
                  <span className="ml-auto text-xs text-gray-400 font-normal flex-shrink-0">
                    {cat.isBirthdayLabel
                      ? t('calendar.fromBirthdays')
                      : cat.todoOwnerName
                      ? t('calendar.fromTodosOf', 'aus To-dos ({name})', {
                          name: cat.todoOwnerName
                        })
                      : t('calendar.fromTodos', 'aus To-dos')}
                  </span>
                </div>
              ) : (
                <input
                  type="text"
                  value={cat.isCustom ? cat.name : ''}
                  onChange={(e) => updateLabel(cat.id, e.target.value)}
                  placeholder={cat.name}
                  className={`flex-1 bg-transparent border-none outline-none text-sm font-medium placeholder:text-gray-400 ${
                    cat.isCustom ? '' : 'text-gray-400'
                  }`}
                  style={cat.isCustom ? { color: cat.color } : {}}
                />
              )}
            </div>
          ))}
          <p className="text-xs text-gray-500 px-3 pt-2">
            {t('calendar.labelResetTip')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LabelManager;
